import { Typography, useBreakpoint } from "@povio/ui";
import { useQueryClient } from "@tanstack/react-query";
import Image from "next/image";
import { useCallback, useEffect } from "react";

import BoneBadgeImage from "@/assets/images/rewards/bone-badge.png";
import useChatSocket from "@/components/chat/useChatSocket";
import { LoyaltyQueries } from "@/data/loyalty/loyalty.queries";
import { useToast } from "@/hooks/useToast";
import { logger } from "@/util/logger";

interface AchievementCompletedEvent {
  userId: string;
  achievementId: string;
  achievementCode: string;
  achievementName: string;
  points: number;
  balanceAfter: number;
}

export const LoyaltySocketSubscriber = () => {
  const { chatSocket } = useChatSocket();
  const { neutralToast } = useToast();
  const queryClient = useQueryClient();
  const isDesktop = useBreakpoint("lg");

  const subscribeToLoyaltyEvents = useCallback(() => {
    if (!chatSocket) {
      return;
    }

    logger.info("[LoyaltySocketSubscriber] Subscribing to loyalty events");

    chatSocket.on("achievement-completed", (data: AchievementCompletedEvent) => {
      logger.info("[LoyaltySocketSubscriber] Achievement completed", data);

      // Show toast with achievement name and points
      neutralToast(
        {
          text: (
            <>
              <Typography
                size="label-2"
                className="text-interactive-text-primary-idle"
                as="span"
              >
                +{data.points}
              </Typography>{" "}
              {data.achievementName}
            </>
          ),
          icon: () => (
            <Image
              src={BoneBadgeImage.src}
              alt="Bone Badge"
              width={24}
              height={24}
              className="h-6 w-6"
            />
          ),
          variant: "outlined",
          position: isDesktop ? "bottom-right" : "bottom-center",
        },
        {
          autoClose: 3000,
        },
      );

      // Invalidate points query to refetch balance
      queryClient.invalidateQueries({ queryKey: LoyaltyQueries.keys.getPoints() });

      // Invalidate rewards list to update unlock status
      queryClient.invalidateQueries({ queryKey: LoyaltyQueries.keys.listRewards() });
    });
  }, [chatSocket, neutralToast, queryClient, isDesktop]);

  const unsubscribeFromLoyaltyEvents = useCallback(() => {
    if (chatSocket) {
      logger.info("[LoyaltySocketSubscriber] Unsubscribing from loyalty events");
      chatSocket.off("achievement-completed");
    }
  }, [chatSocket]);

  useEffect(() => {
    subscribeToLoyaltyEvents();
    return () => {
      unsubscribeFromLoyaltyEvents();
    };
  }, [subscribeToLoyaltyEvents, unsubscribeFromLoyaltyEvents]);

  // This component doesn't render anything
  return null;
};
